import { notification } from 'antd'
import { useRef, useState } from 'react'

import { CityDTO, StreetDTO } from 'services/yandexApi/types'
import { yApi } from 'services/yandexApi/yApi'
import { OrderLocationViewModel } from 'sections/LKSection/modules/OrderModule/stores/models/OrderLocationViewModel'
import { debounce } from 'utils/helpers/debounce'
import { isGeDomain } from 'utils/helpers/domains/getDomain'
import { Country } from 'shared/types/types'

export const usePickPointLocation = (location: OrderLocationViewModel) => {
  const [isCityLoading, setIsCityLoading] = useState(false)
  const [isStreetLoading, setIsStreetLoading] = useState(false)
  const [longitude, setLongitude] = useState('')
  const [latitude, setLatitude] = useState('')

  const timerRef = useRef<NodeJS.Timeout | null>(null)

  const geocoderLanguage = isGeDomain() ? 'en' : 'ru'

  /**
   * @param value "${longitude} ${latitude}" если type === "both"
   */
  const getAddressFromCoordinates = async (
    value: string,
    type: 'longitude' | 'latitude' | 'both',
    country: Country,
  ) => {
    setIsCityLoading(true)
    setIsStreetLoading(true)

    let coordinates = ''
    if (type === 'both') {
      coordinates = value
    } else {
      coordinates = `${type === 'longitude' ? value : longitude} ${type === 'latitude' ? value : latitude}`
    }

    const { data: cities } = await yApi.getCitiesByCoords(coordinates, country, geocoderLanguage)
    const streets = cities.length ? await yApi.getHousesByCoordinates(coordinates, geocoderLanguage) : []

    cityChangeHandler(cities[0])
    streetChangeHandler(streets[0], false)

    setIsCityLoading(false)
    setIsStreetLoading(false)
  }

  const handleCoordinates = (value: string, type: 'latitude' | 'longitude', country: Country) => {
    const normalizedValue = value.replace(/[^\d.]*/g, '')

    if (type === 'latitude') {
      setLatitude(normalizedValue)
    } else {
      setLongitude(normalizedValue)
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    timerRef.current = setTimeout(() => {
      getAddressFromCoordinates(normalizedValue, type, country)
    }, 800)
  }

  const searchCity = debounce(async (query: string, callback: (options: CityDTO[]) => void, country: Country) => {
    if (query.trim().length > 2) {
      setIsCityLoading(true)
      const addresses = await yApi.getCitiesByCityName(query, country, geocoderLanguage)
      callback(addresses.data.map((item, index) => ({ ...item, className: `cityItemDropDownItem${index}` })))
      setIsCityLoading(false)
    }
  }, 800)

  const searchStreet = debounce(async (query: string, callback: (options: StreetDTO[]) => void) => {
    if (!location.cityItem) {
      return
    }

    if (query.trim().length > 2) {
      setIsStreetLoading(true)
      const { name, prefix } = location.cityItem

      try {
        const resp = await yApi.getStreet(`${name}, ${prefix}, ${query}`, geocoderLanguage)
        callback(resp.map((item, index) => ({ ...item, className: `streetItemInput${index}` })))
        location.setStreetOptions(resp)
      } catch (error) {
        notification.error({
          message: 'Ошибка загрузки улиц.',
          description: (error as Error).message,
        })
      } finally {
        setIsStreetLoading(false)
      }
    }
  }, 800)

  const cityChangeHandler = (item: CityDTO) => {
    location.setCityItem(item)
  }

  const streetChangeHandler = (value: StreetDTO | null, changeCoordinates: boolean = true) => {
    location.setStreetItem(value, changeCoordinates)

    if (changeCoordinates && value) {
      setLongitude(value.lng)
      setLatitude(value.lat)
    }
  }

  return {
    longitude,
    latitude,
    isCityLoading,
    isStreetLoading,
    searchCity,
    searchStreet,
    handleCoordinates,
    cityChangeHandler,
    streetChangeHandler,
    getAddressFromCoordinates,
  }
}
